import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { canEditPurchasingPolicy } from '../../utils/purchasingAccess';
import { crmUsersToOrgPeople } from '../../utils/orgChart';
import {
  APPROVAL_GATE_IDS,
  APPROVAL_GATE_LABELS,
  parsePurchasingPolicy,
} from '../../utils/purchasingPolicy';
import { savePurchasingPolicy, subscribePurchasingPolicy } from '../../utils/purchasingPolicySettings';
import type { ApprovalGateId, PurchasingAuthorityBand, PurchasingPolicy } from '../../types/purchasing';

function newBand(): PurchasingAuthorityBand {
  return {
    id: `band-${Date.now()}`,
    minAmount: 0,
    maxAmount: 500,
    gates: ['supervisor'],
  };
}

const PurchasingPolicySettingsCard: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const me = crmUsers.find(u => u.uid === user?.id);
  const canEdit = canEditPurchasingPolicy({
    authRole: user?.role,
    designations: me?.designations || user?.designations,
    briefingProfile: me?.executiveBriefing?.profile,
  });

  const [policy, setPolicy] = useState<PurchasingPolicy>(() => parsePurchasingPolicy(undefined));
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    return subscribePurchasingPolicy(loaded => {
      if (!dirtyRef.current) setPolicy(loaded);
    });
  }, []);

  const people = useMemo(() => crmUsersToOrgPeople(crmUsers), [crmUsers]);

  const mark = (next: PurchasingPolicy) => {
    dirtyRef.current = true;
    setDirty(true);
    setStatus('idle');
    setPolicy(next);
  };

  const save = async () => {
    if (!user?.id || !canEdit) return;
    setSaving(true);
    setStatus('idle');
    try {
      await savePurchasingPolicy(policy, { userId: user.id, userName: user.name || user.email });
      dirtyRef.current = false;
      setDirty(false);
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      console.error('Error saving purchasing policy:', err);
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-ops-section" aria-label="Purchasing authority">
      <h4 className="settings-ops-section-title">Purchasing — Delegation of Authority</h4>
      <p className="setting-description">
        Dollar bands and approval gates for purchase requisitions. Changes apply immediately to new
        requests. After a PO is issued, staff can change the amount by up to 10% without new
        approval; larger moves go through the gates again. Job and overhead people resolve from the
        People org chart (Supervisor), the project&apos;s project manager, and CFO / COO
        designations. CapEx always goes to the controller (CFO), not the COO.
      </p>

      {policy.bands.map((band, idx) => (
        <div
          key={band.id}
          style={{
            padding: '14px 16px',
            background: 'var(--color-surface)',
            borderRadius: 8,
            marginBottom: 8,
            border: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
              Min $
              <input
                type="number"
                min={0}
                disabled={!canEdit}
                value={band.minAmount}
                onChange={e => {
                  const bands = policy.bands.slice();
                  bands[idx] = { ...band, minAmount: Math.max(0, Number(e.target.value) || 0) };
                  mark({ ...policy, bands });
                }}
                style={{ display: 'block', marginTop: 4, width: 110, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
              />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
              Max $ (blank = open)
              <input
                type="number"
                min={0}
                disabled={!canEdit}
                value={band.maxAmount ?? ''}
                onChange={e => {
                  const raw = e.target.value;
                  const bands = policy.bands.slice();
                  bands[idx] = { ...band, maxAmount: raw === '' ? null : Math.max(0, Number(raw) || 0) };
                  mark({ ...policy, bands });
                }}
                style={{ display: 'block', marginTop: 4, width: 140, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
              />
            </label>
            {canEdit && policy.bands.length > 1 && (
              <button
                type="button"
                className="save-button"
                style={{ background: 'transparent', color: 'var(--color-error-text)', boxShadow: 'none' }}
                onClick={() => mark({ ...policy, bands: policy.bands.filter(b => b.id !== band.id) })}
              >
                <Trash2 size={14} /> Remove band
              </button>
            )}
          </div>
          <GatePicker
            label="Job gates"
            gates={band.gates}
            disabled={!canEdit}
            onChange={gates => {
              const bands = policy.bands.slice();
              bands[idx] = { ...band, gates };
              mark({ ...policy, bands });
            }}
          />
          <GatePicker
            label="Overhead gates (optional override)"
            gates={band.overheadGates || []}
            disabled={!canEdit}
            allowEmpty
            onChange={overheadGates => {
              const bands = policy.bands.slice();
              bands[idx] = { ...band, overheadGates: overheadGates.length ? overheadGates : undefined };
              mark({ ...policy, bands });
            }}
          />
          <GatePicker
            label="CapEx gates (optional; default is Controller)"
            gates={band.capexGates || []}
            disabled={!canEdit}
            allowEmpty
            onChange={capexGates => {
              const bands = policy.bands.slice();
              bands[idx] = { ...band, capexGates: capexGates.length ? capexGates : undefined };
              mark({ ...policy, bands });
            }}
          />
        </div>
      ))}

      {canEdit && (
        <button type="button" className="save-button" style={{ marginBottom: 16 }} onClick={() => mark({ ...policy, bands: [...policy.bands, newBand()] })}>
          <Plus size={14} /> Add band
        </button>
      )}

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
        Sales tax on listed line totals
        <input
          disabled={!canEdit}
          type="number"
          min={0}
          max={100}
          step="0.01"
          value={policy.salesTaxPct}
          onChange={e => mark({ ...policy, salesTaxPct: Number(e.target.value) })}
          style={{ display: 'inline-block', marginTop: 4, width: 120, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
        />{' '}
        %
        <span style={{ display: 'block', marginTop: 4, fontWeight: 400, color: 'var(--color-text-muted)' }}>
          Applied automatically after the employee enters the listed price. Uncheck Tax on a line to skip it.
        </span>
      </label>

      <h5 style={{ margin: '8px 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>Hold reasons</h5>
      <p className="setting-description">
        Reviewers can park a request or issued PO. Cash and quote holds stay hidden from the
        requester unless the reviewer checks “show reason.”
      </p>
      {policy.holdReasons.map((reason, idx) => (
        <div key={reason.id} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
          <input
            disabled={!canEdit}
            value={reason.label}
            onChange={e => {
              const holdReasons = policy.holdReasons.slice();
              holdReasons[idx] = { ...reason, label: e.target.value };
              mark({ ...policy, holdReasons });
            }}
            style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={reason.visibleToRequesterDefault}
              onChange={e => {
                const holdReasons = policy.holdReasons.slice();
                holdReasons[idx] = { ...reason, visibleToRequesterDefault: e.target.checked };
                mark({ ...policy, holdReasons });
              }}
            />
            Show by default
          </label>
        </div>
      ))}

      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
        Fallback approver (used when a gate cannot resolve)
        <select
          disabled={!canEdit}
          value={policy.fallbackApproverUserId || ''}
          onChange={e => mark({ ...policy, fallbackApproverUserId: e.target.value || undefined })}
          style={{ display: 'block', marginTop: 4, minWidth: 240, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
        >
          <option value="">None</option>
          {people.map(p => (
            <option key={p.uid} value={p.uid}>
              {p.displayName}
            </option>
          ))}
        </select>
      </label>

      <h5 style={{ margin: '8px 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>Approved payment methods</h5>
      {policy.paymentMethods.map((method, idx) => (
        <div key={method.id} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
          <input
            disabled={!canEdit}
            value={method.label}
            onChange={e => {
              const paymentMethods = policy.paymentMethods.slice();
              paymentMethods[idx] = { ...method, label: e.target.value };
              mark({ ...policy, paymentMethods });
            }}
            style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6 }}
          />
          {canEdit && (
            <button
              type="button"
              className="save-button"
              style={{ background: 'transparent', color: 'var(--color-error-text)', boxShadow: 'none' }}
              onClick={() => mark({ ...policy, paymentMethods: policy.paymentMethods.filter(m => m.id !== method.id) })}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      {canEdit && (
        <button
          type="button"
          className="save-button"
          style={{ marginTop: 4 }}
          onClick={() =>
            mark({
              ...policy,
              paymentMethods: [
                ...policy.paymentMethods,
                { id: `method-${Date.now()}`, label: 'New method' },
              ],
            })
          }
        >
          <Plus size={14} /> Add payment method
        </button>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
          <button className="save-button" disabled={saving || !dirty} onClick={() => void save()}>
            {saving ? 'Saving...' : status === 'saved' ? '\u2713 Saved' : 'Save Purchasing Policy'}
          </button>
          {status === 'error' && (
            <span style={{ fontSize: 13, color: 'var(--color-error-text)' }}>Could not save — try again.</span>
          )}
        </div>
      )}
    </section>
  );
};

function GatePicker({
  label,
  gates,
  disabled,
  allowEmpty,
  onChange,
}: {
  label: string;
  gates: ApprovalGateId[];
  disabled: boolean;
  allowEmpty?: boolean;
  onChange: (gates: ApprovalGateId[]) => void;
}) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {gates.map((gate, idx) => (
          <span
            key={`${gate}-${idx}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 999,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--pm-accent)',
            }}
          >
            {idx + 1}. {APPROVAL_GATE_LABELS[gate]}
            {!disabled && (allowEmpty || gates.length > 1) && (
              <button
                type="button"
                aria-label={`Remove ${APPROVAL_GATE_LABELS[gate]}`}
                onClick={() => onChange(gates.filter((_, i) => i !== idx))}
                style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <select
            value=""
            onChange={e => {
              const gate = e.target.value as ApprovalGateId;
              if (gate) onChange([...gates, gate]);
            }}
            style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12 }}
          >
            <option value="">Add gate…</option>
            {APPROVAL_GATE_IDS.map(id => (
              <option key={id} value={id}>
                {APPROVAL_GATE_LABELS[id]}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

export default PurchasingPolicySettingsCard;
